import React, { useEffect, useState } from 'react';
import { List, Skeleton, Tooltip, Button, Modal } from 'antd';
import { BookOutlined, CalendarOutlined, FileTextOutlined, PaperClipOutlined, EyeOutlined, ExportOutlined } from '@ant-design/icons';
import Utils from '../../../misc/Utils';
import moment from 'moment';
import WysiwygMathEditor from '../../../components/wysiwyg/WysiwygMathEditor';
import DocumentViewer from '../../../components/document-viewer/DocumentViewer';
import {
   getMicrocredentialsTaskModule,
   detectMicrocredentialsTaskMode,
   MICROCREDENTIALS_TASK_MODES,
} from '../../../config/microcredentialsTaskModules';
import { getStudentTaskInfo } from '../../../services/MicrocredentialsStudentTaskService';
import '../../../pages/HomeworksPage/Components/Submissions.css';

function getAssignmentDate(record) {
   return record?.performance_task_date ?? record?.homework_date ?? record?.task_date ?? '';
}

function getSubmissionDate(record) {
   return record?.submit_date ?? record?.submission_date ?? '';
}

function getTitle(record, fallbackLabel) {
   return record?.title ?? record?.Title ?? record?.performance_task_title ?? fallbackLabel;
}

function getDescription(record) {
   return record?.description ?? record?.Description ?? '';
}

function getLevel(record) {
   return record?.level ?? record?.Level ?? '-';
}

function getSubject(record) {
   return record?.subject ?? record?.Subject ?? record?.subject_name ?? '-';
}

function resolveStoredDocumentUrl(storedPath, subdomain) {
   const raw = String(storedPath || '').trim();

   if (!raw) return null;
   if (/^(https?:)?\/\//i.test(raw) || /^(data|blob):/i.test(raw)) return raw;
   if (raw.startsWith('/')) return `${process.env.REACT_APP_API_S3_URL}${raw}`;
   if (subdomain && raw.startsWith(`${subdomain}/`)) {
      return `${process.env.REACT_APP_API_S3_URL}/${raw}`;
   }

   return `${process.env.REACT_APP_API_S3_URL}/${raw}`;
}

function parsePerformanceTaskDocuments(documentValue, subdomain) {
   if (!documentValue) return [];

   try {
      const parsed = JSON.parse(documentValue);

      if (Array.isArray(parsed)) {
         return parsed
            .filter((item) => item && item.s3_path)
            .map((item, index) => ({
               key: `document-${index}`,
               name: item.original_filename || item.s3_path.split('/').pop() || `Attachment ${index + 1}`,
               path: item.s3_path,
               url: resolveStoredDocumentUrl(item.s3_path, subdomain),
            }));
      }
   } catch (error) {
      // Fall back to legacy comma-separated values.
   }

   return String(documentValue)
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean)
      .map((path, index) => ({
         key: `document-${index}`,
         name: path.split('/').pop() || path,
         path,
         url: resolveStoredDocumentUrl(path, subdomain),
      }));
}

export default function StudentPerformanceTaskDetails({
   performanceTaskID,
   mode = detectMicrocredentialsTaskMode(window.location?.pathname),
}) {
   const moduleConfig = getMicrocredentialsTaskModule(mode);
   const taskLabel = moduleConfig.label;

   const [performanceTaskData, setPerformanceTaskData] = useState(null);
   const [uploadedFiles, setUploadedFiles] = useState([]);
   const [previewDocument, setPreviewDocument] = useState(null);
   const [previewDocumentName, setPreviewDocumentName] = useState(null);
   const [previewModalVisible, setPreviewModalVisible] = useState(false);
   const subdomain = localStorage.getItem('lms-subdomain');

   useEffect(() => {
      Utils.verifyJwt();

      if (performanceTaskID) {
         getStudentTaskInfo(performanceTaskID, mode).then((response) => {
            if (response) {
               const data = Array.isArray(response) ? response[0] : response;
               setPerformanceTaskData(data);
               setUploadedFiles(parsePerformanceTaskDocuments(data?.document, subdomain));
            }
         }).catch(() => {
            setPerformanceTaskData(null);
            setUploadedFiles([]);
         });
      }
   }, [mode, performanceTaskID, subdomain]);

   const handlePreviewUploaded = (item) => {
      setPreviewDocument(item?.url || null);
      setPreviewDocumentName(item?.name || null);
      setPreviewModalVisible(true);
   };

   const handleClosePreview = () => {
      setPreviewModalVisible(false);
      setPreviewDocument(null);
      setPreviewDocumentName(null);
   };

   return (
      <>
         {performanceTaskData !== null
            ? (
               <>
                  <div className="homework-panel-header">
                     <div>
                        <span className="homework-panel-kicker">{taskLabel}</span>
                        <h3>Details</h3>
                     </div>
                     <FileTextOutlined />
                  </div>

                  <div className="homework-detail-summary">
                     <div className="homework-detail-hero">
                        <span>Title</span>
                        <strong>{getTitle(performanceTaskData, taskLabel)}</strong>
                     </div>

                     <div className="homework-detail-meta-grid">
                        <div className="homework-detail-meta">
                           <div className="homework-detail-meta-label">
                              <CalendarOutlined />
                              <span>{taskLabel} Date</span>
                           </div>
                           <strong>{getAssignmentDate(performanceTaskData) ? moment(getAssignmentDate(performanceTaskData)).format('MMM. DD, YYYY') : '-'}</strong>
                        </div>
                        <div className="homework-detail-meta">
                           <div className="homework-detail-meta-label">
                              <CalendarOutlined />
                              <span>Submission Date</span>
                           </div>
                           <strong>{getSubmissionDate(performanceTaskData) ? moment(getSubmissionDate(performanceTaskData)).format('MMM. DD, YYYY') : '-'}</strong>
                        </div>
                        <div className="homework-detail-meta">
                           <div className="homework-detail-meta-label">
                              <BookOutlined />
                              <span>Level</span>
                           </div>
                           <strong>{getLevel(performanceTaskData)}</strong>
                        </div>
                        <div className="homework-detail-meta">
                           <div className="homework-detail-meta-label">
                              <BookOutlined />
                              <span>Course</span>
                           </div>
                           <strong>{getSubject(performanceTaskData)}</strong>
                        </div>
                     </div>
                  </div>

                  <div className="homework-field-block">
                     <label>Description</label>
                     <div className="homework-description-box">
                        <WysiwygMathEditor
                           id={`student-performance-task-description-${performanceTaskID || 'details'}`}
                           content={getDescription(performanceTaskData)}
                           height={260}
                           disabled={true}
                        />
                     </div>
                  </div>

                  <div className="homework-field-block">
                     <label>
                        <PaperClipOutlined />
                        Attached Documents
                     </label>
                     {uploadedFiles.length > 0
                        ? (
                           <div className="homework-document-list-scroll">
                              <List
                                 size="small"
                                 dataSource={uploadedFiles}
                                 className="homework-document-list"
                                 renderItem={(item) => (
                                    <List.Item
                                       actions={[
                                          <Tooltip key="preview" placement="top" title="Preview">
                                             <Button
                                                type="text"
                                                size="small"
                                                shape="circle"
                                                icon={<EyeOutlined />}
                                                onClick={() => handlePreviewUploaded(item)}
                                             />
                                          </Tooltip>,
                                          <Tooltip key="open" placement="top" title="Open in new tab">
                                             <a
                                                href={item.url}
                                                target="_blank"
                                                rel="noreferrer"
                                             >
                                                <ExportOutlined />
                                             </a>
                                          </Tooltip>,
                                       ]}
                                    >
                                       <Tooltip placement="right" title={item.name}>
                                          <span className="homework-modal-uploaded-file">{Utils.GetFileIcon(item.name)} {item.name}</span>
                                       </Tooltip>
                                    </List.Item>
                                 )}
                              />
                           </div>
                        )
                        : <div className="homework-empty-documents">No attached documents</div>}
                  </div>
               </>
            )
            : <div className="homework-panel-skeleton">
               <Skeleton.Input active className="homework-skeleton-title" />
               <Skeleton active paragraph={{ rows: 3 }} title={false} />
               <Skeleton.Input active className="homework-skeleton-title" />
               <Skeleton active paragraph={{ rows: 2 }} title={false} />
            </div>}

         <Modal
            title="Document Preview"
            centered
            destroyOnClose={true}
            open={previewModalVisible}
            width="80%"
            footer={[
               <Button key="close" shape="round" onClick={() => handleClosePreview()}>
                  Close
               </Button>,
            ]}
            onCancel={() => handleClosePreview()}
         >
            <div className="modal-document-viewer homework-document-preview-viewer">
               <DocumentViewer
                  document={previewDocument}
                  documentname={previewDocumentName}
                  dvheight="calc(100dvh - 220px)"
                  ismodal={true}
               />
            </div>
         </Modal>
      </>
   );
}
